/**
 * Properties Panel Component
 *
 * Displays and allows editing of properties for selected geometry objects.
 * Shows surface properties like name, type, area, boundary condition, and construction.
 */

import { useState, useMemo, useCallback } from 'react';
import { useGeometryStore } from '../geometryStore';
import { useIDFStore } from '../../stores/idfStore';
import { findSurfaceById, findFenestrationById, findZoneById } from '../extractGeometry';
import { ResizablePanel } from '@/components/ResizablePanel';
import type { SurfaceGeometry, FenestrationGeometry, ZoneGeometry } from '../types';

interface PropertiesPanelProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

type SelectedObjectType = 'surface' | 'fenestration' | 'zone' | 'none';

interface SelectedObjectInfo {
  type: SelectedObjectType;
  surface?: SurfaceGeometry | undefined;
  fenestration?: FenestrationGeometry | undefined;
  zone?: ZoneGeometry | undefined;
}

/**
 * Calculate the area of a surface from its vertices.
 */
function calculateSurfaceArea(vertices: Array<{ x: number; y: number; z: number }>): number {
  if (vertices.length < 3) return 0;

  // Use the Shoelace formula for 3D polygons
  let area = 0;
  const n = vertices.length;
  const v0 = vertices[0];
  if (!v0) return 0;

  for (let i = 1; i < n - 1; i++) {
    const v1 = vertices[i];
    const v2 = vertices[i + 1];
    if (!v1 || !v2) continue;

    // Cross product of (v1-v0) x (v2-v0)
    const ax = v1.x - v0.x;
    const ay = v1.y - v0.y;
    const az = v1.z - v0.z;
    const bx = v2.x - v0.x;
    const by = v2.y - v0.y;
    const bz = v2.z - v0.z;

    const cx = ay * bz - az * by;
    const cy = az * bx - ax * bz;
    const cz = ax * by - ay * bx;

    area += Math.sqrt(cx * cx + cy * cy + cz * cz);
  }

  return area / 2;
}

/**
 * Format boundary condition for display.
 */
function formatBoundaryCondition(condition: string): string {
  const map: Record<string, string> = {
    outdoors: 'Outdoors',
    ground: 'Ground',
    surface: 'Adjacent Surface',
    adiabatic: 'Adiabatic',
    zone: 'Zone',
  };
  return map[condition] ?? condition;
}

/**
 * Format surface type for display.
 */
function formatSurfaceType(type: string): string {
  const map: Record<string, string> = {
    wall: 'Wall',
    floor: 'Floor',
    ceiling: 'Ceiling',
    roof: 'Roof',
  };
  return map[type] ?? type;
}

/**
 * Format fenestration type for display.
 */
function formatFenestrationType(type: string): string {
  const map: Record<string, string> = {
    window: 'Window',
    door: 'Door',
    'glass-door': 'Glass Door',
    skylight: 'Skylight',
  };
  return map[type] ?? type;
}

export function PropertiesPanel({ isCollapsed = false, onToggleCollapse }: PropertiesPanelProps) {
  const geometry = useGeometryStore((s) => s.geometry);
  const selectedIds = useGeometryStore((s) => s.selectedIds);
  const document = useIDFStore((s) => s.document);
  const updateObject = useIDFStore((s) => s.updateObject);
  const getObjectById = useIDFStore((s) => s.getObjectById);

  const [isEditing, setIsEditing] = useState(false);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  // Get available constructions
  const availableConstructions = useMemo(() => {
    if (!document) return [];
    return document.objects
      .filter((obj) => obj.className.toLowerCase() === 'construction')
      .map((obj) => obj.fields[0]?.value ?? '')
      .filter(Boolean);
  }, [document]);

  // Determine what's selected
  const selectedInfo = useMemo((): SelectedObjectInfo => {
    if (!geometry || selectedIds.length === 0) {
      return { type: 'none' };
    }

    const id = selectedIds[0];
    if (!id) return { type: 'none' };

    // Try to find as surface
    const surface = findSurfaceById(geometry, id);
    if (surface) {
      const zone = geometry.zones.find((z) => z.surfaces.some((s) => s.id === id));
      return { type: 'surface', surface, zone: zone ?? undefined };
    }

    // Try to find as fenestration
    const fenestration = findFenestrationById(geometry, id);
    if (fenestration) {
      return { type: 'fenestration', fenestration };
    }

    // Try to find as zone
    const zone = findZoneById(geometry, id);
    if (zone) {
      return { type: 'zone', zone };
    }

    return { type: 'none' };
  }, [geometry, selectedIds]);

  // Handle construction change
  const handleConstructionChange = useCallback(
    (newConstruction: string) => {
      if (!selectedIds[0]) return;

      const idfObject = getObjectById(selectedIds[0]);
      if (!idfObject) return;

      // Find the construction field index by looking at inlineComment
      // For BuildingSurface:Detailed, construction is usually field index 2
      // The inlineComment often contains "!- Construction Name"
      let constructionFieldIndex = idfObject.fields.findIndex(
        (f) => f.inlineComment?.toLowerCase().includes('construction')
      );

      // Fallback to index 2 for BuildingSurface:Detailed if not found
      if (constructionFieldIndex < 0 && idfObject.className.toLowerCase().includes('surface')) {
        constructionFieldIndex = 2;
      }

      if (constructionFieldIndex >= 0 && constructionFieldIndex < idfObject.fields.length) {
        const newFields = [...idfObject.fields];
        const existingField = newFields[constructionFieldIndex];
        if (existingField) {
          newFields[constructionFieldIndex] = {
            ...existingField,
            value: newConstruction,
          };
          updateObject(selectedIds[0], { fields: newFields });
        }
      }

      setIsEditing(false);
    },
    [selectedIds, getObjectById, updateObject]
  );

  if (isCollapsed) {
    return (
      <>
        {/* Desktop collapsed state - side strip */}
        <div className="hidden flex-shrink-0 border-l border-border bg-card md:flex">
          <button
            onClick={onToggleCollapse}
            className="flex h-full w-8 items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Expand properties panel"
            title="Expand properties"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
        {/* Mobile collapsed state - floating button */}
        <button
          onClick={onToggleCollapse}
          className="fixed bottom-20 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg md:hidden"
          aria-label="Show properties"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </>
    );
  }

  return (
    <>
      {/* Desktop: Resizable Side panel */}
      <ResizablePanel
        side="right"
        defaultWidth={288}
        minWidth={200}
        maxWidth={450}
        storageKey="geometry-properties-panel"
        className="h-full flex-col"
        hideOnMobile={true}
        mobileBreakpoint="md"
        resizeAriaLabel="Resize properties panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-sm font-medium text-foreground">Properties</h3>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
              aria-label="Collapse properties panel"
              title="Collapse"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
        {selectedInfo.type === 'none' && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <svg
              className="mb-3 h-12 w-12 text-muted-foreground/50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
              />
            </svg>
            <p className="text-sm text-muted-foreground">
              {selectedIds.length === 0
                ? 'Select an object to view its properties'
                : 'Multiple objects selected'}
            </p>
            {selectedIds.length > 1 && (
              <p className="mt-1 text-xs text-muted-foreground/70">
                {selectedIds.length} objects selected
              </p>
            )}
          </div>
        )}

        {/* Surface Properties */}
        {selectedInfo.type === 'surface' && selectedInfo.surface && (
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Name
              </label>
              <p className="text-sm font-medium text-foreground">{selectedInfo.surface.name}</p>
            </div>

            {/* Type & Zone */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Type
                </label>
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedInfo.surface.type === 'wall'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                      : selectedInfo.surface.type === 'floor'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                        : selectedInfo.surface.type === 'ceiling'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  }`}
                >
                  {formatSurfaceType(selectedInfo.surface.type)}
                </span>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Zone
                </label>
                <p className="text-sm text-foreground">{selectedInfo.zone?.name ?? selectedInfo.surface.zoneName}</p>
              </div>
            </div>

            {/* Area */}
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Area
              </label>
              <p className="text-sm text-foreground">
                {calculateSurfaceArea(selectedInfo.surface.vertices).toFixed(2)} m²
              </p>
            </div>

            {/* Boundary Condition */}
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Boundary Condition
              </label>
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                  selectedInfo.surface.boundaryCondition === 'outdoors'
                    ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300'
                    : selectedInfo.surface.boundaryCondition === 'ground'
                      ? 'bg-stone-100 text-stone-800 dark:bg-stone-900/30 dark:text-stone-300'
                      : selectedInfo.surface.boundaryCondition === 'surface'
                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800/30 dark:text-gray-300'
                }`}
              >
                {formatBoundaryCondition(selectedInfo.surface.boundaryCondition)}
              </span>
            </div>

            {/* Exposure */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 rounded-full ${
                    selectedInfo.surface.sunExposure
                      ? 'bg-yellow-400'
                      : 'bg-muted-foreground/30'
                  }`}
                />
                <span className="text-xs text-muted-foreground">
                  {selectedInfo.surface.sunExposure ? 'Sun Exposed' : 'No Sun'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 rounded-full ${
                    selectedInfo.surface.windExposure
                      ? 'bg-cyan-400'
                      : 'bg-muted-foreground/30'
                  }`}
                />
                <span className="text-xs text-muted-foreground">
                  {selectedInfo.surface.windExposure ? 'Wind Exposed' : 'No Wind'}
                </span>
              </div>
            </div>

            {/* Construction */}
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Construction
              </label>
              {isEditing ? (
                <select
                  value={selectedInfo.surface.construction}
                  onChange={(e) => handleConstructionChange(e.target.value)}
                  onBlur={() => setIsEditing(false)}
                  autoFocus
                  className="w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Select construction...</option>
                  {availableConstructions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="group flex w-full items-center justify-between rounded border border-transparent px-2 py-1 text-left text-sm text-foreground hover:border-border hover:bg-muted"
                  title="Click to change construction"
                >
                  <span>{selectedInfo.surface.construction || 'None'}</span>
                  <svg
                    className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Fenestrations count */}
            {selectedInfo.surface.fenestrations.length > 0 && (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Fenestrations
                </label>
                <p className="text-sm text-foreground">
                  {selectedInfo.surface.fenestrations.length} window
                  {selectedInfo.surface.fenestrations.length > 1 ? 's' : ''}/door
                  {selectedInfo.surface.fenestrations.length > 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* Vertices */}
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Vertices ({selectedInfo.surface.vertices.length})
              </label>
              <div className="max-h-32 overflow-auto rounded border border-border bg-muted/50 p-2 text-xs font-mono text-muted-foreground">
                {selectedInfo.surface.vertices.map((v, i) => (
                  <div key={i}>
                    {i + 1}: ({v.x.toFixed(2)}, {v.y.toFixed(2)}, {v.z.toFixed(2)})
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Fenestration Properties */}
        {selectedInfo.type === 'fenestration' && selectedInfo.fenestration && (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Name
              </label>
              <p className="text-sm font-medium text-foreground">{selectedInfo.fenestration.name}</p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Type
              </label>
              <span className="inline-flex rounded-full bg-cyan-100 px-2 py-0.5 text-xs font-medium text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
                {formatFenestrationType(selectedInfo.fenestration.type)}
              </span>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Parent Surface
              </label>
              <p className="text-sm text-foreground">{selectedInfo.fenestration.parentSurface}</p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Area
              </label>
              <p className="text-sm text-foreground">
                {calculateSurfaceArea(selectedInfo.fenestration.vertices).toFixed(2)} m²
              </p>
            </div>

            {selectedInfo.fenestration.construction && (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Construction
                </label>
                <p className="text-sm text-foreground">{selectedInfo.fenestration.construction}</p>
              </div>
            )}
          </div>
        )}

        {/* Zone Properties */}
        {selectedInfo.type === 'zone' && selectedInfo.zone && (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Zone Name
              </label>
              <p className="text-sm font-medium text-foreground">{selectedInfo.zone.name}</p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Surfaces
              </label>
              <p className="text-sm text-foreground">{selectedInfo.zone.surfaces.length} surfaces</p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Total Fenestrations
              </label>
              <p className="text-sm text-foreground">
                {selectedInfo.zone.surfaces.reduce((sum, s) => sum + s.fenestrations.length, 0)}{' '}
                windows/doors
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Floor Area
              </label>
              <p className="text-sm text-foreground">
                {selectedInfo.zone.surfaces
                  .filter((s) => s.type === 'floor')
                  .reduce((sum, s) => sum + calculateSurfaceArea(s.vertices), 0)
                  .toFixed(2)}{' '}
                m²
              </p>
            </div>
          </div>
        )}
      </div>

        {/* Footer with source line info */}
        {selectedInfo.type === 'surface' && selectedInfo.surface?.sourceLine !== undefined && (
          <div className="border-t border-border bg-muted/50 px-4 py-2">
            <p className="text-xs text-muted-foreground">
              IDF Line: {selectedInfo.surface.sourceLine}
            </p>
          </div>
        )}
      </ResizablePanel>

      {/* Mobile: Bottom sheet */}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-xl border-t border-border bg-card shadow-xl transition-[height] duration-300 ease-out md:hidden ${
          isMobileExpanded ? 'h-[85vh]' : 'h-[40vh]'
        }`}
      >
        {/* Handle bar - tap to expand/collapse */}
        <button
          onClick={() => setIsMobileExpanded(!isMobileExpanded)}
          className="flex w-full cursor-pointer items-center justify-center py-3 active:bg-muted/50"
          aria-label={isMobileExpanded ? 'Collapse panel' : 'Expand panel'}
          aria-expanded={isMobileExpanded}
        >
          <div className="h-1.5 w-12 rounded-full bg-muted-foreground/40" />
          <span className="sr-only">{isMobileExpanded ? 'Tap to collapse' : 'Tap to expand'}</span>
        </button>

        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-foreground">Properties</h3>
            <button
              onClick={() => setIsMobileExpanded(!isMobileExpanded)}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={isMobileExpanded ? 'Collapse' : 'Expand'}
            >
              <svg
                className={`h-4 w-4 transition-transform duration-200 ${isMobileExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Close properties panel"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-auto p-4">
          {selectedInfo.type === 'none' && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-sm text-muted-foreground">
                {selectedIds.length === 0
                  ? 'Select an object to view properties'
                  : `${selectedIds.length} objects selected`}
              </p>
            </div>
          )}

          {/* Surface Properties - Compact mobile layout */}
          {selectedInfo.type === 'surface' && selectedInfo.surface && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-foreground">{selectedInfo.surface.name}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      selectedInfo.surface.type === 'wall'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : selectedInfo.surface.type === 'floor'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                    }`}
                  >
                    {formatSurfaceType(selectedInfo.surface.type)}
                  </span>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      selectedInfo.surface.boundaryCondition === 'outdoors'
                        ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800/30 dark:text-gray-300'
                    }`}
                  >
                    {formatBoundaryCondition(selectedInfo.surface.boundaryCondition)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground">Zone</span>
                  <p className="text-foreground">{selectedInfo.zone?.name ?? selectedInfo.surface.zoneName}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Area</span>
                  <p className="text-foreground">{calculateSurfaceArea(selectedInfo.surface.vertices).toFixed(2)} m²</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Construction</span>
                  <p className="text-foreground">{selectedInfo.surface.construction || 'None'}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Vertices</span>
                  <p className="text-foreground">{selectedInfo.surface.vertices.length}</p>
                </div>
              </div>
            </div>
          )}

          {/* Fenestration Properties - Compact */}
          {selectedInfo.type === 'fenestration' && selectedInfo.fenestration && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-foreground">{selectedInfo.fenestration.name}</p>
                <span className="mt-1 inline-flex rounded-full bg-cyan-100 px-2 py-0.5 text-xs font-medium text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
                  {formatFenestrationType(selectedInfo.fenestration.type)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground">Parent</span>
                  <p className="text-foreground">{selectedInfo.fenestration.parentSurface}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Area</span>
                  <p className="text-foreground">{calculateSurfaceArea(selectedInfo.fenestration.vertices).toFixed(2)} m²</p>
                </div>
              </div>
            </div>
          )}

          {/* Zone Properties - Compact */}
          {selectedInfo.type === 'zone' && selectedInfo.zone && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">{selectedInfo.zone.name}</p>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground">Surfaces</span>
                  <p className="text-foreground">{selectedInfo.zone.surfaces.length}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Windows</span>
                  <p className="text-foreground">
                    {selectedInfo.zone.surfaces.reduce((sum, s) => sum + s.fenestrations.length, 0)}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Floor Area</span>
                  <p className="text-foreground">
                    {selectedInfo.zone.surfaces
                      .filter((s) => s.type === 'floor')
                      .reduce((sum, s) => sum + calculateSurfaceArea(s.vertices), 0)
                      .toFixed(1)} m²
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PropertiesPanel;
